import {
  V4_TYPE3_OFFSETS,
  assertType3Packet,
  clampToRange,
  toByte,
  writeUint16LE,
} from './PerformanceType3';

/**
 * 4.0 协议滚轮设置字段层（command=4 / type=3 读，type=4 写）。
 *
 *   49 wheelDelayEnable      0 关闭 / 1 使能
 *   50-51 wheelDelayTimeStart uint16 LE，1ms 为单位：无效时间
 *   52-53 wheelDelayTimeEnd   uint16 LE，1ms 为单位：有效时长
 *
 * 两个时间共用同一条自首次滚动起算的时间线：时间线总长就是有效时长
 * （End），前 Start 毫秒滚轮无效，其余时间（End - Start）滚轮有效。
 * 因此无效时间不能超过有效时长（Start ≤ End），两者相等意味着时间线
 * 上没有有效段。协议默认 Start=1（1ms）、End=2000（2000ms）。
 *
 * 同属 type3 的 wheelReverseEnable(48) 由「鼠标设置」页维护，本模块不碰——
 * type3 是整包回写，一个字节只能有一个页面负责。
 */

/** 协议单位：1 = 1ms */
export const WHEEL_DELAY_UNIT_MS = 1;

/** 协议默认值（单位是协议刻度，即毫秒） */
export const WHEEL_DELAY_DEFAULTS = Object.freeze({
  enabled: 0,
  start: 1,
  end: 2000,
});

/**
 * 界面可选区间（毫秒）。协议本身是 uint16（最大 65535ms），
 * 这里收敛到实际可用的范围，步进就是协议的一个刻度。
 */
export const WHEEL_DELAY_RANGE_MS = Object.freeze({
  min: 0,
  max: 5000,
  step: WHEEL_DELAY_UNIT_MS,
});

const TICK_RANGE = Object.freeze({
  min: WHEEL_DELAY_RANGE_MS.min / WHEEL_DELAY_UNIT_MS,
  max: WHEEL_DELAY_RANGE_MS.max / WHEEL_DELAY_UNIT_MS,
});

export const wheelTicksToMs = (ticks) =>
  Math.max(0, Math.round(Number(ticks) || 0)) * WHEEL_DELAY_UNIT_MS;

export const wheelMsToTicks = (ms) =>
  clampToRange(Number(ms) / WHEEL_DELAY_UNIT_MS, TICK_RANGE, 0);

/** 单个时间值夹到界面区间并对齐到刻度 */
export const clampWheelDelayMs = (ms) => {
  const clamped = clampToRange(ms, WHEEL_DELAY_RANGE_MS, 0);
  return Math.round(clamped / WHEEL_DELAY_UNIT_MS) * WHEEL_DELAY_UNIT_MS;
};

/** 无效时间以有效时长封顶：Start ≤ End 是时间线模型的硬约束 */
export const clampWheelStartMs = (ms, endMs) =>
  Math.min(clampWheelDelayMs(ms), clampWheelDelayMs(endMs));

/** 有效时长不允许被压到无效时间以下 */
export const clampWheelEndMs = (ms, startMs) =>
  Math.max(clampWheelDelayMs(ms), clampWheelDelayMs(startMs));

/** 时间线上的有效段时长（有效时长减去前段的无效时间），最小为 0 */
export const wheelValidSpanMs = ({ startMs, endMs }) =>
  Math.max(0, clampWheelDelayMs(endMs) - clampWheelDelayMs(startMs));

/**
 * 无效时间与有效时长相同（或有效时长为 0）时，时间线上没有有效段，
 * 滚轮会整段失效。这是个合法但通常不是本意的配置，界面提示用户确认。
 */
export const isWheelWindowEmpty = (settings) => wheelValidSpanMs(settings) <= 0;

/** 时间线总长（= 有效时长，自首次滚动起算），时间线展示用 */
export const wheelWindowTotalMs = ({ endMs }) => clampWheelDelayMs(endMs);

/**
 * type3 解析结果 → 界面模型（毫秒）。
 * @param {Object} parsed - DeviceDataStore.parseV4PerformanceType3 的结果
 * @returns {Object|null}
 */
export function decodeWheelSettings(parsed) {
  if (!parsed || parsed.wheelDelayEnable == null) return null;

  const endMs = clampWheelDelayMs(
    wheelTicksToMs(parsed.wheelDelayTimeEnd ?? WHEEL_DELAY_DEFAULTS.end)
  );
  return {
    delayEnabled: toByte(parsed.wheelDelayEnable) === 1,
    // 旧固件配置可能无效时间比有效时长还长，读入时按时间线约束夹紧
    startMs: clampWheelStartMs(
      wheelTicksToMs(parsed.wheelDelayTimeStart ?? WHEEL_DELAY_DEFAULTS.start),
      endMs
    ),
    endMs,
    // 只读展示：这两个字节归其他页面写
    reverseEnabled: toByte(parsed.wheelReverseEnable) === 1,
    wakeupEnabled: toByte(parsed.wheelWakeupEnable) === 1,
  };
}

/** 界面模型 → 协议字段值，写包与本地回填共用同一份换算 */
export function toWheelFields(settings) {
  const endMs = clampWheelDelayMs(settings?.endMs);
  // 写包也守住时间线约束：无效时间永远不会超过有效时长
  return {
    wheelDelayEnable: settings?.delayEnabled ? 1 : 0,
    wheelDelayTimeStart: wheelMsToTicks(
      clampWheelStartMs(settings?.startMs, endMs)
    ),
    wheelDelayTimeEnd: wheelMsToTicks(endMs),
  };
}

/**
 * 把滚轮设置写进已经克隆好的 type3 包，只动 byte49-53。
 *
 * @param {Uint8Array} packet - 由设备回读包克隆出来的 64 字节缓冲
 * @param {Object} settings - 界面模型
 * @returns {Object} 实际写入的字段值（时间是协议刻度）
 */
export function applyWheelSettingsToPacket(packet, settings) {
  assertType3Packet(packet);

  const fields = toWheelFields(settings);
  packet[V4_TYPE3_OFFSETS.wheelDelayEnable] = fields.wheelDelayEnable;
  writeUint16LE(
    packet,
    V4_TYPE3_OFFSETS.wheelDelayTimeStart,
    fields.wheelDelayTimeStart
  );
  writeUint16LE(
    packet,
    V4_TYPE3_OFFSETS.wheelDelayTimeEnd,
    fields.wheelDelayTimeEnd
  );
  return fields;
}
